import { useCallback, useEffect, useRef, useState } from 'react';

const VALID_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
const MAX_SIZE = 10 * 1024 * 1024; // 10MB

interface Options {
  /** Listen for clipboard paste only while the hosting form is open. */
  active: boolean;
  /** Fired after a pasted image lands, so the host can toast. */
  onPasted?: () => void;
}

/**
 * Image attachments shared by the bug-report, feature-request, and market
 * conditions forms: file upload, clipboard paste, remove, and replace (after
 * annotation in ScreenshotEditor). Images are kept as data URLs because that
 * is what every consuming endpoint accepts.
 */
export function useImageAttachments({ active, onPasted }: Options) {
  const [images, setImages] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const onPastedRef = useRef(onPasted);
  onPastedRef.current = onPasted;

  const readFile = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result;
      if (typeof result === 'string') setImages(prev => [...prev, result]);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    Array.from(files).forEach(file => {
      if (!VALID_TYPES.includes(file.type)) {
        setError(`Invalid file type: ${file.name}. Only PNG, JPG, JPEG, and GIF are allowed.`);
        return;
      }
      if (file.size > MAX_SIZE) {
        setError(`File too large: ${file.name}. Maximum size is 10MB.`);
        return;
      }
      readFile(file);
    });
    e.target.value = '';
  }, [readFile]);

  const removeImage = useCallback((index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  }, []);

  const updateImage = useCallback((index: number, next: string) => {
    setImages(prev => prev.map((img, i) => i === index ? next : img));
  }, []);

  const reset = useCallback(() => { setImages([]); setError(null); }, []);

  useEffect(() => {
    if (!active) return;
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (!item.type.startsWith('image/')) continue;
        const file = item.getAsFile();
        if (!file) continue;
        if (!VALID_TYPES.includes(file.type)) {
          setError('Invalid file type. Only PNG, JPG, JPEG, and GIF are allowed.');
          return;
        }
        if (file.size > MAX_SIZE) {
          setError('File too large. Maximum size is 10MB.');
          return;
        }
        const reader = new FileReader();
        reader.onload = (event) => {
          const result = event.target?.result;
          if (typeof result === 'string') {
            setImages(prev => [...prev, result]);
            onPastedRef.current?.();
          }
        };
        reader.readAsDataURL(file);
        e.preventDefault();
        break; // Only the first image on the clipboard.
      }
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [active]);

  return { images, setImages, error, setError, handleFileInput, removeImage, updateImage, reset };
}
